import { Logger } from "../utils/Logger.js";

/**
 * Service for collecting fragments and calculating bounding boxes
 */
export class FragmentCollector {
	constructor(viewer) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("FragmentCollector");
		this.fragmentCache = new Map();
	}

	/**
	 * Get unified bounding box for all fragments of a dbId
	 * @param {number} dbId - Database ID
	 * @returns {Object|null} THREE.Box3 or null if no valid bounds
	 */
	getUnifiedBoundingBox(dbId) {
		// Check cache first
		const cacheKey = `bbox_${dbId}`;
		if (this.fragmentCache.has(cacheKey)) {
			return this.fragmentCache.get(cacheKey);
		}

		const model = this.viewer.model;
		if (!model) {
			this.logger.warn("No model available");
			return null;
		}

		// Use specialized 2D bounds calculation for 2D models
		if (model.is2d()) {
			const bounds2D = this.get2DObjectBounds(dbId);
			this.fragmentCache.set(cacheKey, bounds2D);
			return bounds2D;
		}

		// For 3D models, use the existing logic
		const fragList = model.getFragmentList();
		const instanceTree = model.getInstanceTree();

		if (!instanceTree || !fragList) {
			this.logger.warn("Instance tree or fragment list not available");
			return null;
		}

		const nodeBBox = new window.THREE.Box3();
		let validBoxFound = false;

		// Collect all fragments for this dbId
		instanceTree.enumNodeFragments(dbId, (fragId) => {
			const fragBBox = new window.THREE.Box3();
			fragList.getWorldBounds(fragId, fragBBox);

			if (this.isValidBoundingBox(fragBBox)) {
				nodeBBox.union(fragBBox);
				validBoxFound = true;
			}
		});

		const result = validBoxFound ? nodeBBox : null;

		// Cache the result
		this.fragmentCache.set(cacheKey, result);

		if (result) {
			this.logger.debug(`Unified bounding box for dbId ${dbId}`, {
				min: result.min,
				max: result.max,
				center: result.getCenter(),
				size: result.getSize(new window.THREE.Vector3()),
			});
		} else {
			this.logger.warn(`No valid bounding box found for dbId ${dbId}`);
		}

		return result;
	}

	/**
	 * Collect all fragment IDs for a given dbId
	 * @param {number} dbId - Database ID
	 * @returns {Promise<number[]>} Array of fragment IDs
	 */
	async collectFragmentIds(dbId) {
		return new Promise((resolve) => {
			const model = this.viewer.model;
			const instanceTree = model?.getInstanceTree();

			if (!instanceTree) {
				this.logger.warn(`Instance tree not available for dbId ${dbId}`);
				resolve([]);
				return;
			}

			const fragmentIds = [];
			instanceTree.enumNodeFragments(dbId, (fragId) => {
				fragmentIds.push(fragId);
			});

			this.logger.debug(
				`Collected ${fragmentIds.length} fragments for dbId ${dbId}`
			);
			resolve(fragmentIds);
		});
	}

	/**
	 * Get fragment data including bounds
	 * @param {number} fragId - Fragment ID
	 * @returns {Object|null} Fragment data with bounds
	 */
	getFragmentData(fragId) {
		const model = this.viewer.model;
		if (!model) return null;

		const fragList = model.getFragmentList();
		if (!fragList) return null;

		const bounds = new window.THREE.Box3();
		fragList.getWorldBounds(fragId, bounds);

		return {
			fragId,
			bounds,
			isValid: this.isValidBoundingBox(bounds),
		};
	}

	/**
	 * Check if a bounding box is valid
	 * @param {Object} bbox - THREE.Box3 bounding box
	 * @returns {boolean} True if valid
	 */
	isValidBoundingBox(bbox) {
		return (
			bbox &&
			bbox.min.x !== Infinity &&
			bbox.max.x !== -Infinity &&
			!bbox.isEmpty()
		);
	}

	/**
	 * Get 2D fragment bounds for 2D views
	 * @param {number} fragId - Fragment ID
	 * @returns {Object|null} 2D bounds or null
	 */
	get2DFragmentBounds(fragId) {
		try {
			const model = this.viewer.model;
			if (!model || !model.is2d()) return null;

			const fragList = model.getFragmentList();
			if (!fragList) return null;

			// For 2D views, use getWorldBounds method from fragment list
			const bbox = new window.THREE.Box3();
			fragList.getWorldBounds(fragId, bbox);

			if (this.isValidBoundingBox(bbox)) {
				return bbox;
			}

			// Fallback: try using fragment proxy getWorldBounds method
			const fragProxy = this.viewer.impl.getFragmentProxy(model, fragId);
			if (fragProxy) {
				const fallbackBbox = new window.THREE.Box3();
				fragProxy.getWorldBounds(fallbackBbox);
				return this.isValidBoundingBox(fallbackBbox) ? fallbackBbox : null;
			}

			return null;
		} catch (error) {
			this.logger.error(
				`Error getting 2D fragment bounds for fragId ${fragId}`,
				error
			);
			return null;
		}
	}

	/**
	 * Get accurate 2D bounds for an object using BoundsCallback utility
	 * This method is more accurate for 2D views as it calculates bounds from actual primitives
	 * @param {number} dbId - Database ID
	 * @returns {Object|null} 2D bounds or null
	 */
	get2DObjectBounds(dbId) {
		try {
			const model = this.viewer.model;
			if (!model || !model.is2d()) return null;

			const fragList = model.getFragmentList();
			if (!fragList) return null;

			let bounds = new window.THREE.Box3();
			let boundsCallback = new window.Autodesk.Viewing.Private.BoundsCallback(
				bounds
			);

			// Find all fragments including this object's primitives
			const fragIds = fragList.fragments.dbId2fragId[dbId];
			if (!fragIds) return null;

			const fragmentIds = Array.isArray(fragIds) ? fragIds : [fragIds];

			for (const fragId of fragmentIds) {
				// Get the actual mesh with all geometry data for given fragment
				const mesh = fragList.getVizmesh(fragId);
				if (!mesh || !mesh.geometry) continue;

				const vbr = new window.Autodesk.Viewing.Private.VertexBufferReader(
					mesh.geometry,
					this.viewer.impl.use2dInstancing
				);

				// Update bounds based on all primitives linked to our dbId
				vbr.enumGeomsForObject(dbId, boundsCallback);
			}

			return this.isValidBoundingBox(bounds) ? bounds : null;
		} catch (error) {
			this.logger.error(
				`Error getting 2D object bounds for dbId ${dbId}`,
				error
			);
			return null;
		}
	}

	/**
	 * Calculate object center point
	 * @param {number} dbId - Database ID
	 * @param {string} strategy - 'top', 'center', 'bottom'
	 * @returns {Object|null} World position or null
	 */
	calculateObjectCenter(dbId, strategy = "top") {
		const bbox = this.getUnifiedBoundingBox(dbId);
		if (!bbox) return null;

		const center = bbox.center();

		switch (strategy) {
			case "top":
				center.y = bbox.max.y;
				break;
			case "bottom":
				center.y = bbox.min.y;
				break;
			case "center":
			default:
				// center is already calculated
				break;
		}

		return center;
	}

	/**
	 * Clear fragment cache
	 */
	clearCache() {
		this.fragmentCache.clear();
		this.logger.debug("Fragment cache cleared");
	}

	/**
	 * Get cache statistics
	 * @returns {Object} Cache stats
	 */
	getCacheStats() {
		return {
			size: this.fragmentCache.size,
			keys: Array.from(this.fragmentCache.keys()),
		};
	}
}
